import { Component, HostListener, inject, signal, computed } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { LoadingService } from '../../../../core/services/loading.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MsalService } from '@azure/msal-angular';
import { UserAccount } from '../../../../core/models/user-account.model';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TenantService } from '../../services/tenant.service';
import { AccountService } from '../../../../core/services/user-account.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { CreateTenantRequest } from '../../models/create-tenant.request';
import { SearchAccountsRequest } from '../../../../core/models/search-accounts.request';

@Component({
  selector: 'app-add-tenant-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatDividerModule
  ],
  templateUrl: './add-tenant-dialog.component.html',
  styleUrl: './add-tenant-dialog.component.css'
})
export class AddTenantDialogComponent {
  constructor (private dialogRef: MatDialogRef<AddTenantDialogComponent>, private fb: FormBuilder) {
    this.addTenantForm = this.fb.group({
      tenantName: ['', [
        Validators.required,
      ]],
      tenantAlias: ['', [
        Validators.required,
      ]],
      selectedAccount: ['', [
        Validators.required,
      ]],
      subscriptionId: [''],
      collectUserLocation: [false],
      hasDashboardsAccess: [true],
      hasPresentationsAccess: [true],
      hasCognitionAccess: [false]
    });

    // Subscribe to form changes to update signal
    this.addTenantForm.valueChanges.subscribe(() => {
      this.formValue.set(this.addTenantForm.getRawValue());
    });
  }
  loadingService = inject(LoadingService);
  readonly authService = inject(MsalService);
  tenantService = inject(TenantService);
  accountService = inject(AccountService);

  accountList = signal<UserAccount[]>([]);

  addTenantForm: FormGroup;
  isSubmitting: boolean = false;
  searched: boolean = false;

  accountErrorMessage: string = '';
  submissionErrorMessage: string = '';

  // loading signal booleans
  readonly loadingFormSubmit = signal(false);
  readonly loadingAccountSearch = signal(false);
  readonly loadingMyAccount = signal(false);
  private readonly formValue = signal<any>({});

  onSubmit(): void {
    this.loadingFormSubmit.set(true);

    if (this.addTenantForm.valid) {
      this.isSubmitting = true;
      this.submissionErrorMessage = "";

      const tenantName: string = this.addTenantForm.get('tenantName')?.value;
      const tenantAlias: string = this.addTenantForm.get('tenantAlias')?.value;
      const ownerUserAccount: UserAccount = this.addTenantForm.get('selectedAccount')?.value;
      let marketplaceSubscriptionId: string | null = this.addTenantForm.get('subscriptionId')?.value;
      if (marketplaceSubscriptionId === '') {
        marketplaceSubscriptionId = null;
      }
      const collectUserLocation: boolean = this.addTenantForm.get('collectUserLocation')?.value;
      const hasDashboardsAccess: boolean = this.addTenantForm.get('hasDashboardsAccess')?.value;
      const hasPresentationsAccess: boolean = this.addTenantForm.get('hasPresentationsAccess')?.value;
      const hasCognitionAccess: boolean = this.addTenantForm.get('hasCognitionAccess')?.value;

      const request: CreateTenantRequest = {
        tenantName,
        tenantAlias,
        ownerUserAccountId: ownerUserAccount.userAccountId,
        marketplaceSubscriptionId,
        collectUserLocation,
        hasDashboardsAccess,
        hasPresentationsAccess,
        hasCognitionAccess,
      }
      console.log(request);
      
      this.tenantService.createTenant(request).subscribe({
        next: (tenantId) => {
          this.dialogRef.close(tenantId)
          this.loadingFormSubmit.set(false);
        },
        error: (error: string) => {
          this.isSubmitting = false;
          this.submissionErrorMessage = error;
          this.loadingFormSubmit.set(false);
        }
      });
    }
  }

  onSearch(search: string): void {
    this.loadingAccountSearch.set(true);
    this.searched = true;
    this.accountErrorMessage = "";
    
    const request: SearchAccountsRequest = {
      searchTerm: search
    }

    this.accountService.searchAccounts(request).subscribe({
      next: (accounts) => {
        this.accountList.set(accounts);
        this.loadingAccountSearch.set(false);
      },
      error: (error: string) => {
        this.accountErrorMessage = "Error searching accounts";
        this.loadingAccountSearch.set(false);
      }
    });
  }

  removeSearch(): void {
    this.addTenantForm.get('selectedAccount')?.setValue(null);
  }

  displayAccount(account: UserAccount): string {
    return account ? `${account.givenName} ${account.surname} (${account.userAccountEmail})` : '';
  }

  selectCurrentAccountAsOwner(): void {
    this.loadingMyAccount.set(true);

    const currentAccount = this.authService.instance.getActiveAccount();
    if (currentAccount) {
      if (currentAccount.username) {
        const request: SearchAccountsRequest = {
          searchTerm: currentAccount.username
        }

        this.accountService.searchAccounts(request).subscribe({
          next: (accounts) => {
            if (accounts != null && accounts.length > 0) {
              this.addTenantForm.get('selectedAccount')?.setValue(accounts[0]);
            }
            else {
              this.accountErrorMessage = "Error getting account";
            }
            this.loadingMyAccount.set(false);
          },
          error: (error: string) => {
            this.accountErrorMessage = "Error getting account";
            this.loadingMyAccount.set(false);
          }
        });
      }  
    }
  }

  showCognitionWarning = computed(() => {
    const currentValue = this.formValue().hasCognitionAccess;
    return currentValue === true;
  });

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
